/**
 * ResetPassword — calm reset-confirm screen.
 *
 * Step 2 of the two-step password reset flow. The emailed link lands here as
 * /reset-password?token=prt_<uuid_hex>. Parent enters a new password (twice),
 * we POST /api/auth/password-reset/confirm; on success we land on /signin
 * with a calm toast.
 *
 * Failure modes (expired/used token, password too short) are surfaced as a
 * single calm error sentence — never a stack trace.
 */
import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Brand from "../components/Brand";
import MetaTags from "../components/MetaTags";

export default function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const valid = useMemo(() => token.length >= 8, [token]);

  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 6) {
      setErr(t("auth.reset.min_length_error"));
      return;
    }
    if (pwd !== confirm) {
      setErr(t("auth.reset.mismatch_error"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", {
        token,
        new_password: pwd,
      });
      setDone(true);
      setTimeout(() => nav("/signin", { replace: true }), 2200);
    } catch (e2) {
      const status = e2.response?.status;
      const detail = e2.response?.data?.detail;
      if (status === 429) {
        setErr(t("auth.reset.rate_limit_calm"));
      } else {
        setErr(
          typeof detail === "string"
            ? detail
            : t("auth.reset.expired_error")
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF9F1]">
      <MetaTags
        title="Choose a new Alouvya password"
        description="Step 2 of your calm password reset. Pick a new password and we'll let you sign back in."
      />

      <div className="px-6 sm:px-12 py-8 flex items-center justify-between">
        <Link to="/" data-testid="reset-brand" className="flex items-center">
          <Brand size="sm" dataTestId="brand-reset" />
        </Link>
        <Link
          to="/signin"
          data-testid="reset-back-to-signin"
          className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1F3A2A]"
        >
          <ArrowLeft size={12} strokeWidth={1.6} />
          {t("auth.forgot.back_to_signin")}
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-md fade-up">
          {!valid ? (
            <div data-testid="reset-invalid-state">
              <span className="eyebrow">{t("auth.reset.invalid_eyebrow")}</span>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-[#1A1B2E]">
                {t("auth.reset.invalid_title_line1")}
                <br />
                <em className="italic text-[#8A9984]">{t("auth.reset.invalid_title_italic")}</em>
              </h1>
              <p className="mt-7 text-[15px] leading-relaxed text-[#6B7280]">
                {t("auth.reset.invalid_body")}
              </p>
              <div className="mt-9 flex gap-3">
                <Link to="/forgot-password" data-testid="reset-request-new" className="btn-primary">
                  {t("auth.reset.invalid_send_new")}
                </Link>
                <Link to="/signin" className="btn-ghost">
                  {t("auth.reset.invalid_signin")}
                </Link>
              </div>
            </div>
          ) : done ? (
            <div data-testid="reset-done-state">
              <span className="eyebrow inline-flex items-center gap-2">
                <ShieldCheck size={14} strokeWidth={1.6} className="text-[#1F3A2A]" />
                {t("auth.reset.done_eyebrow")}
              </span>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-[#1A1B2E]">
                {t("auth.reset.done_title_line1")}
                <br />
                <em className="italic text-[#8A9984]">{t("auth.reset.done_title_italic")}</em>
              </h1>
              <p className="mt-7 text-[15px] leading-relaxed text-[#6B7280]">
                {t("auth.reset.done_body")}
              </p>
              <Link
                to="/signin"
                data-testid="reset-go-signin"
                className="btn-primary mt-9 inline-flex"
              >
                {t("auth.reset.done_go_now")}
              </Link>
            </div>
          ) : (
            <>
              <span className="eyebrow">{t("auth.reset.title_line1")}</span>
              <h1 className="mt-6 font-serif text-4xl sm:text-5xl leading-none tracking-tight text-[#1A1B2E]">
                {t("auth.reset.title_line1")}
                <br />
                <em className="italic text-[#8A9984]">{t("auth.reset.title_italic")}</em>
              </h1>
              <p className="mt-6 text-[15px] leading-relaxed text-[#6B7280]">
                {t("auth.reset.body")}
              </p>

              <form onSubmit={onSubmit} className="mt-10 space-y-8">
                <div>
                  <label htmlFor="reset-new-password" className="eyebrow">{t("auth.reset.new_password")}</label>
                  <input
                    id="reset-new-password"
                    type="password"
                    required
                    autoFocus
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    className="orbit-input mt-3"
                    placeholder={t("auth.reset.new_placeholder")}
                    data-testid="reset-new-password"
                  />
                </div>
                <div>
                  <label htmlFor="reset-confirm-password" className="eyebrow">{t("auth.reset.confirm_password")}</label>
                  <input
                    id="reset-confirm-password"
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="orbit-input mt-3"
                    placeholder={t("auth.reset.confirm_placeholder")}
                    data-testid="reset-confirm-password"
                  />
                </div>

                {err && (
                  <div
                    data-testid="reset-error"
                    role="alert"
                    aria-live="polite"
                    className="rounded-2xl border border-[#FFD4D4] bg-[#FFE4E4] px-4 py-3 text-[13px] text-[#9B2C2C]"
                  >
                    {err}
                  </div>
                )}

                <div className="pt-2 flex flex-wrap gap-3">
                  <button
                    type="submit"
                    disabled={busy || !pwd || !confirm}
                    data-testid="reset-submit"
                    className="btn-primary"
                  >
                    {busy ? t("auth.reset.submit_pending") : t("auth.reset.submit")}
                  </button>
                  <Link to="/signin" className="btn-ghost" data-testid="reset-cancel">
                    {t("auth.reset.cancel")}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      <div className="px-6 sm:px-12 py-8 text-xs text-[#9CA3AF] tracking-wide text-center">
        Calm, clarity, and trust · Alouvya
      </div>
    </div>
  );
}
